import React from 'react';
import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import { GuideLayout, GuideTitle, Section } from './GuideLayout';

const PLANS = [
  {
    title: '1. Neighborhood route',
    who: 'Households inside the ZIPs you already drive. This is the first campaign, on Google and then on Facebook.',
    offer: 'Pickup, wash and fold, delivery. Price per pound, a minimum that covers the drive, and a turnaround you can keep. The ad names two or three neighborhoods, not the whole metro.',
    where: 'Google Search for “wash and fold” and “laundry pickup” in that city. Facebook in a 5 to 10 mile radius with a photo of folded clothes. The link is the city page.',
    avoid: 'A discount so deep the second bag is still a loss. “$1 a pound forever” fills the car and empties the week. A first-order note is optional. The listed price has to be the price you want every week.',
  },
  {
    title: '2. Weekly household',
    who: 'People who already booked once. A route of ten weekly bags beats a hundred strangers who try you once.',
    offer: 'Same day each week, bag on the porch, text when it is back. The booking page can take a repeating pickup. Say the weekday in the text you send after the first delivery: “I can grab this every Tuesday.”',
    where: 'Not a new ad. A text to customers you have. Referrals after the second successful drop-off. The public pages stay about pickup and price so new people can still find the route.',
    avoid: 'Asking for a weekly plan before you have been on time once. Trust is the delivery, then the schedule.',
  },
  {
    title: '3. Short-term rental linens',
    who: 'Airbnb and other hosts who change sheets between guests. One host can be several bags a week. Fewer customers, fuller stops.',
    offer: 'Sheets and towels picked up on checkout day and back before the next check-in. Quote a per-pound price or a per-set price you can live with, and a cutoff time. Several units can sit on one commercial account and one invoice.',
    where: 'A Facebook ad aimed at the same city, with a photo of white linens, after the household ad has booked. Also a direct note to hosts you can name. Google searches for “Airbnb laundry service” plus the city are fewer, so add that phrase to the Search campaign only if the household keywords are already spending cleanly.',
    avoid: 'Promising same-day for a whole house before you know the weight. Ask how many beds, then price the stop.',
  },
  {
    title: '4. Salon, barber, and gym towels',
    who: 'A shop that goes through towels every day and is tired of the machine in the back. One address, a standing weekday.',
    offer: 'A set number of towels, picked up and returned on a named day, billed on one invoice. This is a sales visit more than an ad. The Facebook version is a second ad with a stack of towels and the line “Towel pickup for salons and gyms in CITY.”',
    where: 'Walk in or message ten shops on the streets you already drive. Spend ad money here only after the neighborhood campaign is on. Search volume for “salon towel service” is thin in a small city.',
    avoid: 'A price copied from a household pound rate if their towels are heavy and daily. Price the stop, not just the pound.',
  },
  {
    title: '5. Comforters and bulky items',
    who: 'People who will not fit a comforter in a home machine. They search this on purpose, and the order is worth more than a small bag of clothes.',
    offer: 'A comforter price next to wash and fold, with the same pickup. Your service list should include it so the service page and the booking page both show it.',
    where: 'Add the phrases “comforter cleaning CITY” and “duvet cleaning CITY” to the Google campaign as their own ad group, with an ad that mentions comforters and lands on the service page. Do not mix those phrases into the household ad or Google will show the wrong line.',
    avoid: 'Advertising comforters if you have nowhere to wash them this week.',
  },
  {
    title: '6. Apartments and campus, twice a year',
    who: 'Renters and students during move-in and move-out, mostly August and January, plus the weeks midterms hit.',
    offer: 'The same pound price, aimed at one complex or one campus, with pickup at the building. A property manager who will mention you in a resident email is worth more than the ad.',
    where: 'Facebook radius around that campus for three weeks, then turn it off. A printed line with the booking link at the leasing office if they allow it. Google already covers “laundry pickup near me” the rest of the year.',
    avoid: 'A year-round campus ad in a city where you also serve families. You will pay to reach people who leave in May.',
  },
];

export default function CampaignsGuidePage() {
  return (
    <GuideLayout
      title="Wash and fold campaign plans"
      description="Six wash and fold campaigns for a gig worker: neighborhood pickup, weekly households, rental linens, towels, comforters, and campus. Who they are for and where to run them."
      path="/slb/get-customers/campaigns"
    >
      <GuideTitle
        kicker="What to offer"
        heading="Campaigns that fit a one-person route."
        lede="These are the wash and fold offers that show up again and again for independent pickup: a neighborhood price, a weekly bag, linens for rentals, towels for shops, comforters, and a short campus push. Run them in this order. Each one uses the booking page you already have."
      />

      <Section title="The order">
        <Text color="gray.700">
          Start with the neighborhood. Turn the people who book into a weekly stop. Then add one commercial offer, rentals or towels, because a single extra stop can be worth a day of household bags. Comforters are a search you attach to Google when you can actually wash them. Campus is a calendar, not a lifestyle.
        </Text>
      </Section>

      <VStack align="stretch" spacing={5}>
        {PLANS.map((plan) => (
          <Box key={plan.title} borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={{ base: 4, md: 5 }}>
            <Heading as="h2" fontSize="xl" mb={3}>{plan.title}</Heading>
            <Text fontSize="sm" color="gray.800" mb={2}><Text as="span" fontWeight="700">Who. </Text>{plan.who}</Text>
            <Text fontSize="sm" color="gray.800" mb={2}><Text as="span" fontWeight="700">Offer. </Text>{plan.offer}</Text>
            <Text fontSize="sm" color="gray.800" mb={2}><Text as="span" fontWeight="700">Where it runs. </Text>{plan.where}</Text>
            <Text fontSize="sm" color="gray.800"><Text as="span" fontWeight="700">Leave this out. </Text>{plan.avoid}</Text>
          </Box>
        ))}
      </VStack>
    </GuideLayout>
  );
}
